import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Building2, MapPin, Phone, Mail, AlertCircle, FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';

interface BusinessOnboardingData {
  companyName: string;
  operatingArea: string;
  phone: string;
  email: string;
  description: string;
}

interface BusinessOnboardingModalProps {
  isOpen: boolean;
  onComplete: (data: BusinessOnboardingData) => void;
}

const AREA_OPTIONS = [
  { id: 'helsinki', label: 'Helsinki' },
  { id: 'espoo', label: 'Espoo' },
  { id: 'ppikaupunkiseutu', label: 'Pääkaupunkiseutu' },
];

const BusinessOnboardingModal: React.FC<BusinessOnboardingModalProps> = ({
  isOpen,
  onComplete,
}) => {
  const { t } = useTranslation();
  
  const [companyName, setCompanyName] = useState('');
  const [selectedAreas, setSelectedAreas] = useState<string[]>([]);
  const [district, setDistrict] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [description, setDescription] = useState('');
  const [errors, setErrors] = useState<Record<string, boolean>>({});

  const toggleArea = (label: string) => {
    setSelectedAreas(prev => {
      if (prev.includes(label)) return prev.filter(a => a !== label);
      if (prev.length >= 2) return prev; // max 2
      return [...prev, label];
    });
    if (errors.operatingArea) setErrors(prev => ({ ...prev, operatingArea: false }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, boolean> = {};
    if (!companyName.trim()) newErrors.companyName = true;
    if (selectedAreas.length === 0) newErrors.operatingArea = true;
    if (!phone.trim() && !email.trim()) newErrors.contact = true;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    onComplete({
      companyName: companyName.trim(),
      operatingArea: selectedAreas.join(', '),
      phone: phone.trim(),
      email: email.trim(),
      description: description.trim(),
    });
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-background z-50 overflow-y-auto"
        >
          <div className="min-h-screen flex flex-col">
            {/* Header */}
            <div className="gradient-hero text-primary-foreground px-6 pt-12 pb-8">
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                <div className="w-16 h-16 bg-primary-foreground/20 rounded-2xl flex items-center justify-center mb-4">
                  <Building2 className="w-8 h-8" />
                </div>
                <h1 className="text-2xl font-display font-bold mb-2">{t('setup_business_profile')}</h1>
                <p className="text-primary-foreground/80">{t('setup_business_desc')}</p>
              </motion.div>
            </div>

            {/* Form */}
            <motion.form
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              onSubmit={handleSubmit}
              className="flex-1 px-6 py-6 -mt-4 bg-card rounded-t-3xl"
            >
              <div className="space-y-5">
                {/* Company Name */}
                <div className="space-y-2">
                  <label className="text-sm font-medium flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-muted-foreground" />
                    {t('company_name')} *
                    {errors.companyName && <AlertCircle className="w-4 h-4 text-destructive" />}
                  </label>
                  <Input
                    value={companyName}
                    onChange={(e) => {
                      setCompanyName(e.target.value);
                      if (errors.companyName) setErrors(prev => ({ ...prev, companyName: false }));
                    }}
                    className={cn("h-12 rounded-xl", errors.companyName && "border-destructive")}
                    placeholder={t('company_name_placeholder')}
                  />
                </div>

                {/* Operating Area - Multi-select */}
                <div className="space-y-2">
                  <label className="text-sm font-medium flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-muted-foreground" />
                    {t('operating_area')} * ({t('max_2_areas')})
                    {errors.operatingArea && <AlertCircle className="w-4 h-4 text-destructive" />}
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {AREA_OPTIONS.map((area) => (
                      <button
                        key={area.id}
                        type="button"
                        onClick={() => toggleArea(area.label)}
                        className={cn(
                          "px-4 py-2.5 rounded-xl border-2 font-medium text-sm transition-all",
                          selectedAreas.includes(area.label)
                            ? "border-primary bg-primary/10 text-primary"
                            : "border-border hover:border-primary/30",
                          errors.operatingArea && selectedAreas.length === 0 && "border-destructive"
                        )}
                      >
                        {area.label}
                      </button>
                    ))}
                  </div>
                  </div>

                  {/* District (optional) */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-muted-foreground" />
                      {t('operating_district')} ({t('optional')})
                    </label>
                    <Input
                      value={district}
                      onChange={(e) => setDistrict(e.target.value)}
                      className="h-12 rounded-xl"
                      placeholder={t('operating_district_placeholder')}
                    />
                  </div>
                {/* Contact Info Section */}
                <div className="space-y-4 pt-4 border-t border-border">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold">{t('contact_info')} *</h3>
                    {errors.contact && <AlertCircle className="w-4 h-4 text-destructive" />}
                  </div>
                  <p className="text-sm text-muted-foreground -mt-2">{t('contact_info_hint')}</p>
                  
                  <div className="space-y-2">
                    <label className="text-sm font-medium flex items-center gap-2">
                      <Phone className="w-4 h-4 text-muted-foreground" />
                      {t('phone')}
                    </label>
                    <Input
                      type="tel"
                      value={phone}
                      onChange={(e) => {
                        setPhone(e.target.value);
                        if (errors.contact) setErrors(prev => ({ ...prev, contact: false }));
                      }}
                      className={cn("h-12 rounded-xl", errors.contact && !phone && !email && "border-destructive")}
                      placeholder="+358 40 123 4567"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium flex items-center gap-2">
                      <Mail className="w-4 h-4 text-muted-foreground" />
                      {t('email')}
                    </label>
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (errors.contact) setErrors(prev => ({ ...prev, contact: false }));
                      }}
                      className={cn("h-12 rounded-xl", errors.contact && !phone && !email && "border-destructive")}
                      placeholder="yritys@email.com"
                    />
                  </div>
                </div>

                {/* Description */}
                <div className="space-y-2 pt-4 border-t border-border">
                  <label className="text-sm font-medium flex items-center gap-2">
                    <FileText className="w-4 h-4 text-muted-foreground" />
                    {t('description')} ({t('optional')})
                  </label>
                  <Textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="rounded-xl min-h-[100px] resize-none"
                    placeholder={t('business_description_placeholder')}
                  />
                </div>
              </div>

              <div className="mt-8 pb-8">
                <Button type="submit" className="w-full h-12 rounded-xl shadow-button">
                  {t('continue_to_app')}
                </Button>
              </div>
            </motion.form>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default BusinessOnboardingModal;
